import { FC } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { setToastMessage } from '@stores/App/slice';
import { CustomButton } from '@components';
import { TOAST_TYPES } from '@constants/constants';
import { useUpdateUserAccessMutation } from '@api/clientUserManagement';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import { USER_ACTION_SUCCESS_TOASTS } from '../../constants';

const dialogPaperSx = {
  borderRadius: '28px',
  maxWidth: '520px',
};

interface AssignOwnerRoleModalProps {
  open: boolean;
  clientCode: string;
  selectedUserEmail: string;
  selectedUserMembershipGrantId: string;
  onClose: () => void;
  onSubmit?: () => void;
}

const AssignOwnerRoleModal: FC<AssignOwnerRoleModalProps> = ({
  open,
  clientCode,
  selectedUserEmail,
  selectedUserMembershipGrantId,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [updateUserAccess, { isLoading: isUpdating }] = useUpdateUserAccessMutation();

  const handleClose = () => {
    if (isUpdating) return;
    onClose();
  };

  const handleAssignOwner = async () => {
    if (!selectedUserMembershipGrantId) return;
    try {
      await updateUserAccess({
        clientCode,
        membershipGrantId: selectedUserMembershipGrantId,
        companyRole: CLIENT_USER_ROLE.OWNER,
      }).unwrap();
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.SUCCESS,
          message: USER_ACTION_SUCCESS_TOASTS.ACCESS_GRANTED,
        }),
      );
      onSubmit?.();
      onClose();
    } catch (error) {
      console.error('Error while assigning owner role:', error);
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Error while assigning owner role',
        }),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      aria-labelledby="assign-owner-role-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          ...dialogPaperSx,
          width: isMobile ? 'min(90vw, 400px)' : '520px',
        },
      }}
    >
      <DialogTitle
        id="assign-owner-role-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          py: '1.5rem',
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem', color: '#162936' }}
        >
          Assign Owner Role
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} disabled={isUpdating} sx={{ p: 0 }}>
          <CloseOutlinedIcon sx={{ fontSize: '1.5rem', color: '#162936' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          color: '#162936',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
        }}
      >
        <Typography sx={{ fontWeight: 500 }}>
          Are you sure you want to grant Owner access to this user?
        </Typography>
        <Typography sx={{ fontWeight: 700 }}>{selectedUserEmail}</Typography>
        <Typography sx={{ fontWeight: 500 }}>
          Owners have full access to all dashboards and can manage users, This change will take
          effect immediately.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          padding: '1.5rem',
          gap: '0.5rem',
        }}
      >
        <CustomButton
          name="Cancel"
          onClick={handleClose}
          disabled={isUpdating}
          backgroundColor="#FFF"
          color="#51B8B0"
        />
        <CustomButton
          name={isUpdating ? 'Assigning...' : 'Assign Owner'}
          onClick={handleAssignOwner}
          disabled={isUpdating}
          backgroundColor="#2F736E"
          color="#FFF"
        />
      </DialogActions>
    </Dialog>
  );
};

export default AssignOwnerRoleModal;
